import {v4 as uuidv4} from 'uuid';

import CaptionEditableClass from 'notebook/mediocre/base-plugin/CaptionEditable';
import {makeFormattedTextSearchResultData} from 'notebook/mediocre/formatted-text/FormattedTextSearchResults';
import {MediocreSearchResult, TextMatcher} from 'notebook/mediocre/search/SearchResults';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {TABLE_SEARCH_PATHS} from 'notebook/tables/constants/TableConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import TableUtil from 'notebook/tables/TableUtil';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';

export function doesTableCellMatch(
  matcher: TextMatcher,
  cell: Cell,
  evaluatedCell: EvaluatedCell | null,
  searchOverFormulas: boolean = false
): boolean {
  // If search over formulas is enabled and the cell contains a formula, we search over the raw text of the cell
  return (
    (searchOverFormulas && TableUtil.isCellFormula(cell) && matcher.hasMatch(cell.text)) ||
    // Otherwise, we search over the evaluated cell
    ((!searchOverFormulas || !TableUtil.isCellFormula(cell)) &&
      evaluatedCell != null &&
      matcher.hasMatch(formatTypedValue(evaluatedCell.value)))
  );
}

export function makeTableSearchResultPath(
  prefix: Array<string | number>,
  i: number,
  j: number
): Array<string | number> {
  return [...prefix, i, j];
}

export function buildTableSearchResult(tableCid: string, row: number, column: number): MediocreSearchResult {
  return {
    index: makeTableSearchResultPath([tableCid], row, column),
    uuid: uuidv4(),
    selection: {
      data: {
        multiSelIndex: [row, column],
        multiStartIndex: [row, column],
        multiEndIndex: [row, column],
      },
      multiSelectKey: TableUtil.getTableMultiSelectKey(tableCid),
    },
  };
}

export function* getSearchResultsForTableName(
  matcher: TextMatcher,
  tableName: string,
  tableCid: string
): Generator<MediocreSearchResult> {
  for (const match of matcher.findAllMatches(tableName)) {
    const [matchedText] = match;
    const start = match.index;
    const end = start + matchedText.length;
    yield {
      index: [tableCid, TABLE_SEARCH_PATHS.NAME],
      data: makeFormattedTextSearchResultData(start, end),
      selection: {selection: {cid: CaptionEditableClass.getAttachmentCid(tableCid), start: 0, end: 0}},
      uuid: uuidv4(),
    };
  }
}

export function* getSearchResultsForColumnNames(
  matcher: TextMatcher,
  columnNames: Array<string>,
  tableCid: string
): Generator<MediocreSearchResult> {
  for (const [i, columnName] of columnNames.entries()) {
    if (matcher.hasMatch(columnName)) {
      yield {
        index: [tableCid, TABLE_SEARCH_PATHS.TABLE_HEADER_ROW, i],
        selection: {selection: {cid: CaptionEditableClass.getAttachmentCid(tableCid), start: 0, end: 0}},
        uuid: uuidv4(),
      };
    }
  }
}
